import type {
  ReportDocumentModel,
  ReportPageBlock,
  ReportPageModel,
  ReportSection,
  ReportSectionSegment,
  ReportTableRow,
} from "./reportTypes";

const PAGE_HEIGHT = 1122;
const FIRST_PAGE_HEADER_HEIGHT = 214;
const METRIC_ROW_HEIGHT = 58;
const PAGE_VERTICAL_PADDING = 112;
const CONTINUATION_TOP_CONTEXT_HEIGHT = 42;
const FOOTER_HEIGHT = 62;
const SECTION_HEADER_BASE_HEIGHT = 54;
const SECTION_SUMMARY_HEIGHT = 30;
const TABLE_HEADER_HEIGHT = 32;
const SECTION_GAP_HEIGHT = 14;
const ROW_HEIGHT: Record<"compact" | "normal" | "wrapped", number> = {
  compact: 37,
  normal: 43,
  wrapped: 50,
};

export interface ReportSectionLayoutMeasurements {
  firstSegmentShellHeight: number;
  continuationSegmentShellHeight: number;
  rowHeights: number[];
}

export interface ReportLayoutMeasurements {
  firstPageAvailableHeight: number;
  continuationPageAvailableHeight: number;
  plainPageAvailableHeight: number;
  footerHeight: number;
  sections: ReportSectionLayoutMeasurements[];
}

function rowHeight(row: ReportTableRow) {
  return ROW_HEIGHT[row.density ?? "normal"];
}

function metricRowsHeight(document: ReportDocumentModel) {
  const metricRows =
    document.metricRows ?? (document.metrics ? [document.metrics] : []);

  return metricRows.length * METRIC_ROW_HEIGHT;
}

function sectionHeaderHeight(segment: ReportSectionSegment) {
  if (!segment.showHeader) return 0;
  return (
    SECTION_HEADER_BASE_HEIGHT + (segment.summary ? SECTION_SUMMARY_HEIGHT : 0)
  );
}

function sectionShellHeight(segment: ReportSectionSegment) {
  return (
    sectionHeaderHeight(segment) + TABLE_HEADER_HEIGHT + SECTION_GAP_HEIGHT
  );
}

function availableHeight(pageIndex: number, document: ReportDocumentModel) {
  const firstPageReserved =
    pageIndex === 0 ? FIRST_PAGE_HEADER_HEIGHT + metricRowsHeight(document) : 0;
  const continuationContextReserved =
    pageIndex > 0 && document.continuationContext
      ? CONTINUATION_TOP_CONTEXT_HEIGHT
      : 0;

  return (
    PAGE_HEIGHT -
    PAGE_VERTICAL_PADDING -
    firstPageReserved -
    continuationContextReserved
  );
}

function segmentHeight(segment: ReportSectionSegment) {
  return (
    sectionShellHeight(segment) +
    segment.rows.reduce((sum, row) => sum + rowHeight(row), 0)
  );
}

function makeSegment(
  section: ReportSection,
  rows: ReportTableRow[],
  continuation: boolean,
): ReportSectionSegment {
  return {
    ...section,
    rows,
    continuation,
    showHeader: !continuation,
  };
}

function makePage(
  index: number,
  document: ReportDocumentModel,
): ReportPageModel {
  return {
    index,
    showHeader: index === 0,
    showFooter: false,
    contextLabel: index > 0 ? document.continuationContext : undefined,
    blocks: [],
  };
}

function pageContentHeight(page: ReportPageModel) {
  return page.blocks.reduce(
    (sum, block) => sum + segmentHeight(block.section),
    0,
  );
}

function splitRowsForPage(
  section: ReportSection,
  rows: ReportTableRow[],
  startIndex: number,
  maxHeight: number,
  continuation: boolean,
) {
  let nextIndex = startIndex;
  let height = sectionShellHeight(makeSegment(section, [], continuation));

  while (nextIndex < rows.length) {
    const nextHeight = rowHeight(rows[nextIndex]);

    if (nextIndex > startIndex && height + nextHeight > maxHeight) {
      break;
    }

    height += nextHeight;
    nextIndex += 1;

    if (height > maxHeight) {
      break;
    }
  }

  return nextIndex;
}

function canFitFooter(page: ReportPageModel, document: ReportDocumentModel) {
  return (
    pageContentHeight(page) + FOOTER_HEIGHT <=
    availableHeight(page.index, document)
  );
}

export function buildReportPages(
  document: ReportDocumentModel,
): ReportPageModel[] {
  const pages: ReportPageModel[] = [makePage(0, document)];

  function currentPage() {
    return pages[pages.length - 1];
  }

  function startPage() {
    pages.push(makePage(pages.length, document));
  }

  function appendSegment(segment: ReportSectionSegment) {
    const page = currentPage();
    const height = segmentHeight(segment);

    if (
      page.blocks.length > 0 &&
      pageContentHeight(page) + height > availableHeight(page.index, document)
    ) {
      startPage();
    }

    const targetPage = currentPage();
    targetPage.blocks.push({
      id: `${segment.id}-${targetPage.blocks.length}`,
      type: "section",
      section: segment,
    });
  }

  for (const section of document.sections) {
    if (section.rows.length === 0) {
      appendSegment(makeSegment(section, [], false));
      continue;
    }

    let rowIndex = 0;
    let continuation = false;

    while (rowIndex < section.rows.length) {
      const page = currentPage();
      const remainingHeight =
        availableHeight(page.index, document) - pageContentHeight(page);
      const maxHeight =
        page.blocks.length === 0
          ? availableHeight(page.index, document)
          : remainingHeight;
      const shellSegment = makeSegment(section, [], continuation);

      if (
        maxHeight <
        sectionShellHeight(shellSegment) + rowHeight(section.rows[rowIndex])
      ) {
        startPage();
        continue;
      }

      const nextIndex = splitRowsForPage(
        section,
        section.rows,
        rowIndex,
        maxHeight,
        continuation,
      );
      const segmentRows = section.rows.slice(rowIndex, nextIndex);

      appendSegment(makeSegment(section, segmentRows, continuation));
      continuation = true;
      rowIndex = nextIndex;
    }
  }

  if (pages.length === 0) {
    return [makePage(0, document)];
  }

  let lastPage = currentPage();

  if (canFitFooter(lastPage, document)) {
    lastPage.showFooter = true;
    return pages;
  }

  const lastBlock = lastPage.blocks[lastPage.blocks.length - 1];
  if (!lastBlock) {
    lastPage.showFooter = true;
    return pages;
  }

  const lastRows = lastBlock.section.rows;
  const finalPageAvailable = availableHeight(pages.length, document);

  if (lastRows.length > 1) {
    const movedRows = lastRows.slice(-1);
    const remainingRows = lastRows.slice(0, -1);
    const movedSegment = makeSegment(lastBlock.section, movedRows, true);

    if (segmentHeight(movedSegment) + FOOTER_HEIGHT <= finalPageAvailable) {
      lastBlock.section = {
        ...lastBlock.section,
        rows: remainingRows,
      };
      startPage();
      lastPage = currentPage();
      lastPage.blocks.push({
        id: `${movedSegment.id}-${lastPage.blocks.length}`,
        type: "section",
        section: movedSegment,
      });
      lastPage.showFooter = true;
      return pages;
    }
  }

  if (lastPage.blocks.length > 1) {
    const movedBlock = lastPage.blocks.pop();

    if (
      movedBlock &&
      segmentHeight(movedBlock.section) + FOOTER_HEIGHT <= finalPageAvailable
    ) {
      startPage();
      lastPage = currentPage();
      lastPage.blocks.push({
        id: `${movedBlock.section.id}-${lastPage.blocks.length}`,
        type: "section",
        section: movedBlock.section,
      });
      lastPage.showFooter = true;
      return pages;
    }

    if (movedBlock) {
      lastPage.blocks.push(movedBlock);
    }
  }

  lastPage.showFooter = true;
  return pages.filter((page) => page.blocks.length > 0);
}

type MeasuredPageBlock = ReportPageBlock & {
  sectionIndex: number;
  startRow: number;
  endRow: number;
  continuation: boolean;
  height: number;
};

type MeasuredPage = Omit<ReportPageModel, "blocks"> & {
  blocks: MeasuredPageBlock[];
};

function makeMeasuredPage(
  index: number,
  document: ReportDocumentModel,
): MeasuredPage {
  return {
    ...makePage(index, document),
    blocks: [],
  };
}

function measuredAvailableHeight(
  pageIndex: number,
  document: ReportDocumentModel,
  measurements: ReportLayoutMeasurements,
) {
  if (pageIndex === 0) return measurements.firstPageAvailableHeight;
  return document.continuationContext
    ? measurements.continuationPageAvailableHeight
    : measurements.plainPageAvailableHeight;
}

function measuredSectionShellHeight(
  measurements: ReportLayoutMeasurements,
  sectionIndex: number,
  continuation: boolean,
) {
  const sectionMeasurements = measurements.sections[sectionIndex];
  if (!sectionMeasurements) return TABLE_HEADER_HEIGHT + SECTION_GAP_HEIGHT;
  return continuation
    ? sectionMeasurements.continuationSegmentShellHeight
    : sectionMeasurements.firstSegmentShellHeight;
}

function measuredRowHeight(
  section: ReportSection,
  measurements: ReportLayoutMeasurements,
  sectionIndex: number,
  rowIndex: number,
) {
  const measured = measurements.sections[sectionIndex]?.rowHeights[rowIndex];
  if (measured && measured > 0) return measured;

  const row = section.rows[rowIndex];
  return row ? rowHeight(row) : 0;
}

function measuredSegmentHeight(
  section: ReportSection,
  measurements: ReportLayoutMeasurements,
  sectionIndex: number,
  startRow: number,
  endRow: number,
  continuation: boolean,
) {
  let height = measuredSectionShellHeight(
    measurements,
    sectionIndex,
    continuation,
  );

  for (let rowIndex = startRow; rowIndex < endRow; rowIndex += 1) {
    height += measuredRowHeight(section, measurements, sectionIndex, rowIndex);
  }

  return height;
}

function measuredPageContentHeight(page: MeasuredPage) {
  return page.blocks.reduce((sum, block) => sum + block.height, 0);
}

function toReportPages(pages: MeasuredPage[]): ReportPageModel[] {
  return pages
    .filter((page) => page.blocks.length > 0)
    .map((page) => ({
      ...page,
      blocks: page.blocks.map(({ height: _height, ...block }) => block),
    }));
}

function measuredRowsForPage(
  section: ReportSection,
  measurements: ReportLayoutMeasurements,
  sectionIndex: number,
  startRow: number,
  maxHeight: number,
  continuation: boolean,
) {
  let nextIndex = startRow;
  let height = measuredSectionShellHeight(
    measurements,
    sectionIndex,
    continuation,
  );

  while (nextIndex < section.rows.length) {
    const nextHeight = measuredRowHeight(
      section,
      measurements,
      sectionIndex,
      nextIndex,
    );

    if (nextIndex > startRow && height + nextHeight > maxHeight) {
      break;
    }

    height += nextHeight;
    nextIndex += 1;

    if (height > maxHeight) break;
  }

  return nextIndex;
}

export function buildMeasuredReportPages(
  document: ReportDocumentModel,
  measurements: ReportLayoutMeasurements,
): ReportPageModel[] {
  const pages: MeasuredPage[] = [makeMeasuredPage(0, document)];

  function currentPage() {
    return pages[pages.length - 1];
  }

  function startPage() {
    pages.push(makeMeasuredPage(pages.length, document));
  }

  function appendSegment(
    sectionIndex: number,
    startRow: number,
    endRow: number,
    continuation: boolean,
  ) {
    const section = document.sections[sectionIndex];
    if (!section) return;

    const height = measuredSegmentHeight(
      section,
      measurements,
      sectionIndex,
      startRow,
      endRow,
      continuation,
    );
    const page = currentPage();

    if (
      page.blocks.length > 0 &&
      measuredPageContentHeight(page) + height >
        measuredAvailableHeight(page.index, document, measurements)
    ) {
      startPage();
    }

    const targetPage = currentPage();
    const segment = makeSegment(
      section,
      section.rows.slice(startRow, endRow),
      continuation,
    );
    targetPage.blocks.push({
      id: `${segment.id}-${targetPage.blocks.length}`,
      type: "section",
      section: segment,
      sectionIndex,
      startRow,
      endRow,
      continuation,
      height,
    });
  }

  for (
    let sectionIndex = 0;
    sectionIndex < document.sections.length;
    sectionIndex += 1
  ) {
    const section = document.sections[sectionIndex];
    if (!section) continue;

    if (section.rows.length === 0) {
      appendSegment(sectionIndex, 0, 0, false);
      continue;
    }

    let rowIndex = 0;
    let continuation = false;

    while (rowIndex < section.rows.length) {
      const page = currentPage();
      const available = measuredAvailableHeight(
        page.index,
        document,
        measurements,
      );
      const remainingHeight = available - measuredPageContentHeight(page);
      const maxHeight = page.blocks.length === 0 ? available : remainingHeight;
      const shellHeight = measuredSectionShellHeight(
        measurements,
        sectionIndex,
        continuation,
      );
      const firstRowHeight = measuredRowHeight(
        section,
        measurements,
        sectionIndex,
        rowIndex,
      );

      if (maxHeight < shellHeight + firstRowHeight) {
        if (page.blocks.length > 0) {
          startPage();
          continue;
        }

        appendSegment(sectionIndex, rowIndex, rowIndex + 1, continuation);
        rowIndex += 1;
        continuation = true;
        continue;
      }

      const nextIndex = measuredRowsForPage(
        section,
        measurements,
        sectionIndex,
        rowIndex,
        maxHeight,
        continuation,
      );

      appendSegment(sectionIndex, rowIndex, nextIndex, continuation);
      rowIndex = nextIndex;
      continuation = true;
    }
  }

  if (pages.length === 0) return [makePage(0, document)];

  let lastPage = currentPage();
  if (
    measuredPageContentHeight(lastPage) + measurements.footerHeight <=
    measuredAvailableHeight(lastPage.index, document, measurements)
  ) {
    lastPage.showFooter = true;
    return toReportPages(pages);
  }

  const lastBlock = lastPage.blocks[lastPage.blocks.length - 1];
  if (!lastBlock) {
    lastPage.showFooter = true;
    return toReportPages(pages);
  }

  const finalPageAvailable = measuredAvailableHeight(
    pages.length,
    document,
    measurements,
  );

  if (lastBlock.endRow - lastBlock.startRow > 1) {
    const section = document.sections[lastBlock.sectionIndex];
    if (section) {
      const originalEndRow = lastBlock.endRow;
      const movedStart = originalEndRow - 1;
      const movedHeight = measuredSegmentHeight(
        section,
        measurements,
        lastBlock.sectionIndex,
        movedStart,
        originalEndRow,
        true,
      );

      if (movedHeight + measurements.footerHeight <= finalPageAvailable) {
        lastBlock.endRow = movedStart;
        lastBlock.section = {
          ...lastBlock.section,
          rows: section.rows.slice(lastBlock.startRow, movedStart),
        };
        lastBlock.height = measuredSegmentHeight(
          section,
          measurements,
          lastBlock.sectionIndex,
          lastBlock.startRow,
          movedStart,
          lastBlock.continuation,
        );

        startPage();
        lastPage = currentPage();
        const movedSegment = makeSegment(
          section,
          section.rows.slice(movedStart, originalEndRow),
          true,
        );
        lastPage.blocks.push({
          id: `${movedSegment.id}-${lastPage.blocks.length}`,
          type: "section",
          section: movedSegment,
          sectionIndex: lastBlock.sectionIndex,
          startRow: movedStart,
          endRow: originalEndRow,
          continuation: true,
          height: movedHeight,
        });
        lastPage.showFooter = true;
        return toReportPages(pages);
      }
    }
  }

  if (lastPage.blocks.length > 1) {
    const movedBlock = lastPage.blocks.pop();

    if (
      movedBlock &&
      movedBlock.height + measurements.footerHeight <= finalPageAvailable
    ) {
      startPage();
      lastPage = currentPage();
      lastPage.blocks.push({
        ...movedBlock,
        id: `${movedBlock.section.id}-${lastPage.blocks.length}`,
      });
      lastPage.showFooter = true;
      return toReportPages(pages);
    }

    if (movedBlock) lastPage.blocks.push(movedBlock);
  }

  lastPage.showFooter = true;
  return toReportPages(pages);
}
